import { useSubmit } from "@certn/api";
import { toastQueue } from "@certn/cascadia";
import {
  DropdownMenu,
  GhostIconButton,
  Link,
  MenuItem,
  MenuSection,
  showToast,
  Text,
} from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { DeactivateUserModal } from "./DeactivateUserModal";
import { ReactivateUserModal } from "./ReactivateUserModal";

interface Props {
  id: string;
  name: string | null;
  refreshUser: () => void;
  isActive: boolean;
  showViewUser?: boolean;
}

export function ActionsDropdown({
  id,
  name,
  refreshUser,
  isActive,
  showViewUser = false,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeactivateModal, setShowDeactivateModal] =
    useState<boolean>(false);
  const [showReactivateModal, setShowReactivateModal] =
    useState<boolean>(false);

  const { submit: resetPassword } = useSubmit(
    `/api/internal/client-portal/users/${id}/reset-password/`,
    {
      throwErrors: true,
      showToastOnError: t("There was an error processing the password reset."),
      onSuccess: () => {
        showToast(
          t("Password reset request has been sent to {{name}}.", { name })
        );
      },
    }
  );

  const { submit: reactivateUser, isSubmitting: isReactivateSubmitting } =
    useSubmit(`/api/internal/client-portal/users/${id}/activate/`, {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        const toasts = toastQueue.visibleToasts;
        if (toasts.length > 0) {
          // closes deactivate success toast when undo has been selected
          toastQueue.close(toasts[0].key);
        }
        showToast(
          t("Success. {{name}} has been reactivated.", {
            name,
          }),
          {
            type: "success",
          }
        );
        refreshUser();
      },
    });

  const { submit: deactivateUser, isSubmitting: isDeactivateSubmitting } =
    useSubmit(`/api/internal/client-portal/users/${id}/deactivate/`, {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(
          <>
            {t("Success. {{name}} has been deactivated.", { name })}{" "}
            <Link
              onPress={() => reactivateUser()}
              linkStyle="monochrome"
              testID="undo-deactivate"
            >
              {t("Undo")}
            </Link>
          </>,
          {
            type: "success",
          }
        );
        refreshUser();
      },
    });

  return (
    <>
      <DropdownMenu
        triggerButton={
          <GhostIconButton
            testID="user-expand-button"
            size="small"
            icon="more_horiz"
            aria-label={t("Expand menu")}
          />
        }
        onItemAction={(menuItem) => {
          switch (menuItem) {
            case "view-user":
              navigate(`/settings/user/${id}`, {
                state: {
                  last: {
                    href: `${location.pathname}${location.search}`,
                  },
                },
              });
              break;
            case "reset-password":
              resetPassword();
              break;
            case "deactivate-user":
              setShowDeactivateModal(true);
              break;
            case "reactivate-user":
              setShowReactivateModal(true);
              break;
          }
        }}
        placement="bottom right"
      >
        <MenuSection>
          {showViewUser && (
            <MenuItem id="view-user">
              <Text style="input/text-field" testID="view-user-button">
                {t("View user")}
              </Text>
            </MenuItem>
          )}
          <MenuItem id="reset-password">
            <Text style="input/text-field" testID="reset-password-button">
              {t("Reset password")}
            </Text>
          </MenuItem>
          {isActive && (
            <MenuItem id="deactivate-user">
              <Text
                style="input/text-field"
                color="text-danger"
                testID="deactivate-user-button"
              >
                {t("Deactivate user")}
              </Text>
            </MenuItem>
          )}
          {!isActive && (
            <MenuItem id="reactivate-user">
              <Text style="input/text-field" testID="reactivate-user-button">
                {t("Reactivate user")}
              </Text>
            </MenuItem>
          )}
        </MenuSection>
      </DropdownMenu>
      <DeactivateUserModal
        isOpen={showDeactivateModal}
        isLoading={isDeactivateSubmitting}
        name={name}
        onCancel={() => setShowDeactivateModal(false)}
        onConfirm={() => {
          deactivateUser();
          setShowDeactivateModal(false);
        }}
      />
      <ReactivateUserModal
        isOpen={showReactivateModal}
        isLoading={isReactivateSubmitting}
        name={name}
        onCancel={() => setShowReactivateModal(false)}
        onConfirm={() => {
          reactivateUser();
          setShowReactivateModal(false);
        }}
      />
    </>
  );
}
